/**
 * React Query hook for the Workshop's read path (P4.T1).
 *
 * Loads a Persona + its current PersonaBundle in one effect. Returns
 * `{ persona, bundle: null }` only while a persona genuinely lacks a
 * `current_bundle_id` — a should-not-happen state since v2→core (the
 * Workshop renders an error card, not a legacy mode).
 *
 * Refetches on window focus so multi-tab edits (e.g. via the existing
 * PersonaEditor modal) propagate to an open Workshop.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { apiClient } from '@/services/apiClient'
import type { ApiPersona } from '@/types/persona'
import type { PersonaBundleResponse } from '@/types/personaWizard'

export interface PersonaWithBundle {
  persona: ApiPersona
  bundle: PersonaBundleResponse | null
}

export const personaBundleQueryKey = (personaId: string | null) =>
  ['persona-bundle', personaId] as const

export function usePersonaBundle(
  personaId: string | null,
): UseQueryResult<PersonaWithBundle, Error> {
  return useQuery({
    queryKey: personaBundleQueryKey(personaId),
    enabled: !!personaId,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<PersonaWithBundle> => {
      const persona = await apiClient.get<ApiPersona>(`/api/personas/${personaId}`)
      if (!persona.current_bundle_id) {
        return { persona, bundle: null }
      }
      const bundle = await apiClient.get<PersonaBundleResponse>(
        `/api/persona-bundles/${persona.current_bundle_id}`,
      )
      return { persona, bundle }
    },
  })
}
